import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  MessageSquare,
  Plus,
  Users,
  Clock,
  Pin,
  Lock,
  ChevronLeft,
  Edit,
  Trash2,
  Flag,
  ThumbsUp,
  Search,
} from 'lucide-react';
import type { CommunityTopic } from '@shared/schema';
import type { PaginatedResponse } from '@shared/api-contracts';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { isUnauthorizedError, redirectToLogin } from '@/lib/authUtils';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { authedJson } from '@/lib/http';
import { PublicShell } from '@/components/public-shell';
import { formatDateCompact } from '@/lib/dateUtils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { RichTextEditor, RichTextDisplay } from '@/components/rich-text-editor';

interface UserEnrollmentOption {
  classId: string;
  classTitle: string;
}

interface CommunityTopicListItem extends CommunityTopic {
  replyCount: number;
  likeCount: number;
  userLiked: boolean;
  user: {
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
  };
  class: {
    id: string;
    title: string | null;
  } | null;
}

const EDIT_WINDOW_MS = 15 * 60 * 1000;

interface TopicReply {
  id: string;
  content: string;
  isDeleted: boolean;
  userId: string;
  createdAt: string;
  updatedAt: string;
  userFirstName: string | null;
  userLastName: string | null;
  userProfileImageUrl: string | null;
  likeCount: number;
  userLiked: boolean;
}

interface TopicDetail {
  topic: {
    id: string;
    title: string;
    content: string | null;
    isSticky: boolean;
    isLocked: boolean;
    userId: string;
    createdAt: string;
    updatedAt: string;
    userFirstName: string | null;
    userLastName: string | null;
    likeCount: number;
    userLiked: boolean;
  };
  replies: PaginatedResponse<TopicReply>;
}

function TopicDetailPanel({
  topicId,
  onBack,
  currentUserId,
  isAdmin,
}: {
  topicId: string;
  onBack: () => void;
  currentUserId: string | undefined;
  isAdmin: boolean;
}) {
  const { toast } = useToast();
  const [replyContent, setReplyContent] = useState('');
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ topicId?: string; replyId?: string }>({});
  const [reportReason, setReportReason] = useState('');

  const { data, isLoading } = useQuery<TopicDetail>({
    queryKey: ['/api/community/topics', topicId],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/community/topics/${topicId}`);
      return res.json();
    },
  });

  const replyMutation = useMutation({
    mutationFn: async (content: string) => {
      await apiRequest('POST', `/api/community/topics/${topicId}/replies`, { content });
    },
    onSuccess: () => {
      setReplyContent('');
      queryClient.invalidateQueries({ queryKey: ['/api/community/topics', topicId] });
    },
    onError: () => toast({ title: 'Failed to post reply', variant: 'destructive' }),
  });

  const editReplyMutation = useMutation({
    mutationFn: async ({ replyId, content }: { replyId: string; content: string }) => {
      await apiRequest('PUT', `/api/community/topics/${topicId}/replies/${replyId}`, { content });
    },
    onSuccess: () => {
      setEditingReplyId(null);
      queryClient.invalidateQueries({ queryKey: ['/api/community/topics', topicId] });
    },
    onError: () => toast({ title: 'Failed to edit reply', variant: 'destructive' }),
  });

  const deleteReplyMutation = useMutation({
    mutationFn: async (replyId: string) => {
      await apiRequest('DELETE', `/api/community/topics/${topicId}/replies/${replyId}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['/api/community/topics', topicId] }),
    onError: () => toast({ title: 'Failed to delete reply', variant: 'destructive' }),
  });

  const adminDeleteReplyMutation = useMutation({
    mutationFn: async (replyId: string) => {
      await apiRequest('DELETE', `/api/admin/community/replies/${replyId}`);
    },
    onSuccess: () => {
      toast({ title: 'Reply removed' });
      queryClient.invalidateQueries({ queryKey: ['/api/community/topics', topicId] });
    },
    onError: () => toast({ title: 'Failed to remove reply', variant: 'destructive' }),
  });

  const stickyMutation = useMutation({
    mutationFn: async () => apiRequest('POST', `/api/community/topics/${topicId}/sticky`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['/api/community/topics', topicId] }),
  });

  const lockMutation = useMutation({
    mutationFn: async () => apiRequest('POST', `/api/community/topics/${topicId}/lock`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['/api/community/topics', topicId] }),
  });

  const reportMutation = useMutation({
    mutationFn: async (payload: { topicId?: string; replyId?: string; reason: string }) => {
      await apiRequest('POST', '/api/community/reports', payload);
    },
    onSuccess: () => {
      toast({ title: 'Report submitted' });
      setReportOpen(false);
      setReportReason('');
    },
    onError: (e: Error) =>
      toast({ title: 'Report failed', description: e.message, variant: 'destructive' }),
  });

  const likeTopicMutation = useMutation({
    mutationFn: async ({ topicId, liked }: { topicId: string; liked: boolean }) => {
      const method = liked ? 'DELETE' : 'POST';
      await apiRequest(method, `/api/community/topics/${topicId}/like`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['/api/community/topics', topicId] }),
  });

  const likeReplyMutation = useMutation({
    mutationFn: async ({ replyId, liked }: { replyId: string; liked: boolean }) => {
      const method = liked ? 'DELETE' : 'POST';
      await apiRequest(method, `/api/community/topics/${topicId}/replies/${replyId}/like`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['/api/community/topics', topicId] }),
  });

  if (isLoading) {
    return (
      <div>
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground"
        >
          <ChevronLeft className="w-4 h-4" /> Back to discussions
        </button>
        <div className="animate-pulse h-40 bg-muted rounded-lg" />
      </div>
    );
  }
  if (!data) return null;

  const { topic, replies } = data;

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="w-4 h-4" /> Back to discussions
      </button>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {topic.isSticky && <Pin className="w-4 h-4 text-accent" />}
                {topic.isLocked && <Lock className="w-4 h-4 text-muted-foreground" />}
                <h2 className="text-xl font-semibold">{topic.title}</h2>
              </div>
              <RichTextDisplay html={topic.content} className="text-muted-foreground" />
              <p className="text-xs text-muted-foreground mt-3">
                by {[topic.userFirstName, topic.userLastName].filter(Boolean).join(' ')} ·{' '}
                {formatDateCompact(topic.createdAt)}
              </p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {isAdmin && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => stickyMutation.mutate()}
                    disabled={stickyMutation.isPending}
                  >
                    {topic.isSticky ? 'Unpin' : 'Pin'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => lockMutation.mutate()}
                    disabled={lockMutation.isPending}
                  >
                    {topic.isLocked ? 'Unlock' : 'Lock'}
                  </Button>
                </>
              )}
              {currentUserId && (
                <Button
                  size="sm"
                  variant="ghost"
                  className={topic.userLiked ? 'text-primary' : ''}
                  onClick={() =>
                    likeTopicMutation.mutate({ topicId: topic.id, liked: topic.userLiked })
                  }
                  disabled={likeTopicMutation.isPending}
                  aria-label={topic.userLiked ? 'Unlike topic' : 'Like topic'}
                >
                  <ThumbsUp className="w-4 h-4 mr-1" />
                  {topic.likeCount > 0 && topic.likeCount}
                </Button>
              )}
              {currentUserId && currentUserId !== topic.userId && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setReportTarget({ topicId: topic.id });
                    setReportOpen(true);
                  }}
                >
                  <Flag className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground">
          {replies.total} {replies.total === 1 ? 'reply' : 'replies'}
        </p>
        {replies.data.map(reply => {
          const isAuthor = currentUserId === reply.userId;
          const within = Date.now() - new Date(reply.createdAt).getTime() < EDIT_WINDOW_MS;

          return editingReplyId === reply.id ? (
            <Card key={reply.id}>
              <CardContent className="p-4 space-y-2">
                <RichTextEditor
                  value={editContent}
                  onChange={setEditContent}
                  placeholder="Edit your reply…"
                  minHeight="80px"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      editReplyMutation.mutate({ replyId: reply.id, content: editContent })
                    }
                    disabled={editReplyMutation.isPending}
                  >
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingReplyId(null)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card key={reply.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">
                    {[reply.userFirstName, reply.userLastName].filter(Boolean).join(' ')} ·{' '}
                    {formatDateCompact(reply.createdAt)}
                    {reply.updatedAt !== reply.createdAt && ' (edited)'}
                  </span>
                  <div className="flex items-center gap-1">
                    {isAuthor && within && (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          aria-label="Edit"
                          onClick={() => {
                            setEditingReplyId(reply.id);
                            setEditContent(reply.content);
                          }}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive"
                          aria-label="Delete"
                          onClick={() => deleteReplyMutation.mutate(reply.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    {isAdmin && !isAuthor && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        aria-label="Admin remove"
                        onClick={() => adminDeleteReplyMutation.mutate(reply.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {currentUserId && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className={`h-7 px-2 gap-1 ${reply.userLiked ? 'text-primary' : ''}`}
                        onClick={() =>
                          likeReplyMutation.mutate({ replyId: reply.id, liked: reply.userLiked })
                        }
                        disabled={likeReplyMutation.isPending}
                        aria-label={reply.userLiked ? 'Unlike reply' : 'Like reply'}
                      >
                        <ThumbsUp className="h-3.5 w-3.5" />
                        {reply.likeCount > 0 && <span className="text-xs">{reply.likeCount}</span>}
                      </Button>
                    )}
                    {currentUserId && !isAuthor && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        aria-label="Report"
                        onClick={() => {
                          setReportTarget({ replyId: reply.id });
                          setReportOpen(true);
                        }}
                      >
                        <Flag className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                <RichTextDisplay html={reply.content} className="text-sm text-muted-foreground" />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {!topic.isLocked && currentUserId ? (
        <div className="space-y-2">
          <RichTextEditor
            value={replyContent}
            onChange={setReplyContent}
            placeholder="Write a reply…"
            minHeight="80px"
          />
          <Button
            onClick={() => replyMutation.mutate(replyContent)}
            disabled={
              replyContent.replace(/<[^>]+>/g, '').trim().length < 5 || replyMutation.isPending
            }
          >
            {replyMutation.isPending ? 'Posting…' : 'Post reply'}
          </Button>
        </div>
      ) : topic.isLocked ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Lock className="h-4 w-4" /> This topic is locked.
        </p>
      ) : null}

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report content</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={reportReason}
              onChange={e => setReportReason(e.target.value)}
              placeholder="Describe the issue…"
              rows={4}
            />
            <Button
              onClick={() => reportMutation.mutate({ ...reportTarget, reason: reportReason })}
              disabled={reportReason.trim().length < 3 || reportMutation.isPending}
              className="w-full"
            >
              {reportMutation.isPending ? 'Submitting…' : 'Submit report'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Community() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { toast } = useToast();
  const [selectedClassId, setSelectedClassId] = useState('');
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [newTopicContent, setNewTopicContent] = useState('');
  const [showNewTopicDialog, setShowNewTopicDialog] = useState(false);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: 'Unauthorized',
        description: 'Please log in to access the community.',
        variant: 'destructive',
      });
      redirectToLogin(500);
    }
  }, [isAuthenticated, authLoading, toast]);

  const { data: enrollments = [], error: enrollmentsError } = useQuery<UserEnrollmentOption[]>({
    queryKey: ['/api/enrollments'],
  });

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchQuery), 350);
    return () => clearTimeout(id);
  }, [searchQuery]);

  interface SearchResult {
    id: string;
    title: string;
    scopeType: string;
    createdAt: string;
    user: { firstName: string | null; lastName: string | null };
  }

  const { data: searchResults } = useQuery<{ data: SearchResult[]; total: number }>({
    queryKey: ['/api/community/search', debouncedSearch],
    queryFn: async () =>
      authedJson(`/api/community/search?q=${encodeURIComponent(debouncedSearch)}`),
    enabled: debouncedSearch.length >= 2,
  });

  const {
    data: topicsResponse,
    isLoading: topicsLoading,
    error: topicsError,
  } = useQuery<PaginatedResponse<CommunityTopicListItem>>({
    queryKey: ['/api/community/topics', selectedClassId],
    queryFn: async () => {
      const params = new window.URLSearchParams();
      if (selectedClassId) params.append('classId', selectedClassId);
      return await authedJson<PaginatedResponse<CommunityTopicListItem>>(
        `/api/community/topics?${params}`
      );
    },
  });

  const communityTopics = topicsResponse?.data ?? [];

  const createTopicMutation = useMutation({
    mutationFn: async (topicData: { title: string; content: string; classId: string }) => {
      await apiRequest('POST', '/api/community/topics', topicData);
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Your topic has been created successfully!',
      });
      setNewTopicTitle('');
      setNewTopicContent('');
      setShowNewTopicDialog(false);
      queryClient.invalidateQueries({ queryKey: ['/api/community/topics'] });
    },
    onError: error => {
      if (isUnauthorizedError(error as Error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to create topic. Please try again.',
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    const errors = [enrollmentsError, topicsError].filter(Boolean);
    errors.forEach(error => {
      if (error && isUnauthorizedError(error as Error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
      }
    });
  }, [enrollmentsError, topicsError, toast]);

  const handleCreateTopic = () => {
    if (!newTopicTitle.trim()) {
      toast({
        title: 'Missing Title',
        description: 'Please enter a topic title.',
        variant: 'destructive',
      });
      return;
    }

    if (!newTopicContent.trim()) {
      toast({
        title: 'Missing Content',
        description: 'Please enter topic content.',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedClassId) {
      toast({
        title: 'Class Required',
        description: 'Select one of your enrolled classes before starting a discussion.',
        variant: 'destructive',
      });
      return;
    }

    createTopicMutation.mutate({
      title: newTopicTitle,
      content: newTopicContent,
      classId: selectedClassId,
    });
  };

  if (authLoading) {
    return (
      <PublicShell routeKey="community">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </PublicShell>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <PublicShell routeKey="community">
      {/* Header */}
      <section className="bg-muted py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-foreground mb-4">Community Forum</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Connect with fellow learners, ask questions, and share your progress with the Kalovate
              community.
            </p>
          </div>

          {/* Search */}
          <div className="max-w-4xl mx-auto mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search discussions…"
                className="pl-9"
              />
            </div>
            {debouncedSearch.length >= 2 && searchResults && searchResults.data.length > 0 && (
              <div className="mt-2 border border-border rounded-lg bg-background shadow-lg overflow-hidden">
                {searchResults.data.map(result => (
                  <button
                    key={result.id}
                    type="button"
                    className="w-full text-left px-4 py-3 hover:bg-muted transition-colors border-b border-border last:border-0"
                    onClick={() => {
                      setSelectedTopicId(result.id);
                      setSearchQuery('');
                    }}
                  >
                    <p className="font-medium text-sm text-foreground">{result.title}</p>
                    <p className="text-xs text-muted-foreground">
                      by {result.user.firstName} {result.user.lastName} ·{' '}
                      {formatDateCompact(result.createdAt)}
                    </p>
                  </button>
                ))}
              </div>
            )}
            {debouncedSearch.length >= 2 && searchResults && searchResults.data.length === 0 && (
              <p className="mt-2 text-sm text-muted-foreground px-1">No discussions found.</p>
            )}
          </div>

          {/* Class Filter */}
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Filter by your enrolled classes:
                </label>
                <select
                  value={selectedClassId}
                  onChange={e => setSelectedClassId(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  data-testid="select-class-filter"
                  aria-label="Filter by enrolled class"
                >
                  <option value="">All Discussions</option>
                  {enrollments.map((enrollment: any) => (
                    <option key={enrollment.classId} value={enrollment.classId}>
                      {enrollment.classTitle}
                    </option>
                  ))}
                </select>
              </div>

              <Dialog open={showNewTopicDialog} onOpenChange={setShowNewTopicDialog}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="button-new-topic"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Topic
                  </Button>
                </DialogTrigger>

                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Create New Topic</DialogTitle>
                  </DialogHeader>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Topic Title
                      </label>
                      <Input
                        value={newTopicTitle}
                        onChange={e => setNewTopicTitle(e.target.value)}
                        placeholder="What would you like to discuss?"
                        data-testid="input-topic-title"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Related Class (Optional)
                      </label>
                      <select
                        value={selectedClassId}
                        onChange={e => setSelectedClassId(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                        data-testid="select-topic-class"
                        aria-label="Related class"
                      >
                        <option value="">Select a class</option>
                        {enrollments.map(enrollment => (
                          <option key={enrollment.classId} value={enrollment.classId}>
                            {enrollment.classTitle}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Content
                      </label>
                      <RichTextEditor
                        value={newTopicContent}
                        onChange={setNewTopicContent}
                        placeholder="Share your thoughts, questions, or insights..."
                        minHeight="140px"
                      />
                    </div>

                    <Button
                      onClick={handleCreateTopic}
                      disabled={createTopicMutation.isPending}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid="button-create-topic"
                    >
                      {createTopicMutation.isPending ? 'Creating...' : 'Create Topic'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </section>

      {/* Topics List / Topic Detail */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {selectedTopicId ? (
            <TopicDetailPanel
              topicId={selectedTopicId}
              onBack={() => setSelectedTopicId(null)}
              currentUserId={user?.id}
              isAdmin={user?.role === 'admin'}
            />
          ) : topicsLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-muted rounded-full animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="h-5 bg-muted rounded animate-pulse" />
                        <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : communityTopics.length > 0 ? (
            <div className="space-y-4">
              {communityTopics.map(topic => (
                <Card key={topic.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Avatar className="w-12 h-12">
                        <AvatarImage
                          src={
                            topic.user?.profileImageUrl
                              ? topic.user.profileImageUrl.startsWith('http')
                                ? topic.user.profileImageUrl
                                : `${window.location.origin}${topic.user.profileImageUrl}`
                              : undefined
                          }
                          alt={topic.user?.firstName ?? 'Community member'}
                          data-testid={`topic-avatar-${topic.id}`}
                        />
                        <AvatarFallback>
                          {topic.user?.firstName?.[0]}
                          {topic.user?.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {topic.isSticky && <Pin className="w-4 h-4 text-accent" />}
                            {topic.isLocked && (
                              <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
                            )}
                            <h3
                              className="font-semibold text-foreground hover:text-primary cursor-pointer"
                              data-testid={`topic-title-${topic.id}`}
                              onClick={() => setSelectedTopicId(topic.id)}
                            >
                              {topic.title}
                            </h3>
                          </div>

                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{formatDateCompact(topic.createdAt)}</span>
                          </div>
                        </div>

                        <p
                          className="text-muted-foreground mb-3 line-clamp-2"
                          data-testid={`topic-content-${topic.id}`}
                        >
                          {topic.content
                            ? topic.content
                                .replace(/<[^>]+>/g, ' ')
                                .replace(/\s+/g, ' ')
                                .trim()
                            : ''}
                        </p>

                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>
                            by{' '}
                            <strong>
                              {topic.user?.firstName} {topic.user?.lastName}
                            </strong>
                          </span>

                          {topic.class && (
                            <Badge variant="outline" className="text-xs">
                              {topic.class.title}
                            </Badge>
                          )}

                          <div className="flex items-center gap-1">
                            <MessageSquare className="w-4 h-4" />
                            <span>{topic.replyCount || 0} replies</span>
                          </div>

                          {topic.likeCount > 0 && (
                            <div className="flex items-center gap-1">
                              <ThumbsUp className="w-4 h-4" />
                              <span>{topic.likeCount}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="text-2xl font-bold text-foreground mb-2">No Discussions Yet</h3>
              <p className="text-muted-foreground mb-6">
                {selectedClassId
                  ? 'No discussions for this class yet. Be the first to start a conversation!'
                  : 'No discussions in your enrolled classes yet. Start the first topic!'}
              </p>
              <Button
                onClick={() => setShowNewTopicDialog(true)}
                data-testid="button-start-first-topic"
              >
                Start a Discussion
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Community Guidelines — only shown in list view */}
      {!selectedTopicId && (
        <section className="py-16 bg-muted">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Community Guidelines
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6 text-sm text-muted-foreground">
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Be Respectful</h4>
                    <p>
                      Treat all community members with kindness and respect. We're all here to learn
                      and grow together.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Stay on Topic</h4>
                    <p>
                      Keep discussions relevant to the classes and learning topics. This helps
                      everyone find valuable information.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Help Each Other</h4>
                    <p>
                      Share your knowledge and experience. Answer questions when you can and
                      celebrate others' achievements.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">No Spam</h4>
                    <p>
                      Avoid repetitive posts, excessive self-promotion, or irrelevant content that
                      doesn't add value to discussions.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}
    </PublicShell>
  );
}
